import { toast } from "sonner";

export type ApiErrorKind = "network" | "client" | "server" | "application";

export interface ApiErrorOptions {
  fallbackMessage?: string;
  source?: string;
  status?: number;
  context?: Record<string, unknown>;
  /** Skip the toast; still logs to console. Use when the caller renders its own fallback UI. */
  silent?: boolean;
}

export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

const DEFAULT_MESSAGE = "Noe gikk galt, prøv igjen";
const NETWORK_MESSAGE =
  "Kunne ikke kontakte serveren. Sjekk internett-tilkoblingen og prøv igjen.";
const SERVER_MESSAGE =
  "Serveren svarte ikke som forventet. Prøv igjen om litt.";
const CLIENT_MESSAGE =
  "Forespørselen ble avvist. Prøv igjen, eller kontakt oss hvis problemet vedvarer.";

function resolveStatus(error: unknown, explicit?: number): number | undefined {
  if (typeof explicit === "number" && Number.isFinite(explicit)) return explicit;
  if (error instanceof HttpError) return error.status;
  if (typeof error === "object" && error !== null && "status" in error) {
    const raw = (error as { status: unknown }).status;
    const parsed = typeof raw === "number" ? raw : Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

export function classifyApiError(
  error: unknown,
  explicitStatus?: number,
): { kind: ApiErrorKind; status?: number } {
  const status = resolveStatus(error, explicitStatus);
  if (typeof status === "number") {
    if (status >= 500) return { kind: "server", status };
    if (status >= 400) return { kind: "client", status };
  }
  // `fetch` rejects with TypeError on offline/DNS/CORS failures
  if (error instanceof TypeError) return { kind: "network" };
  return { kind: "application", status };
}

function defaultMessageFor(kind: ApiErrorKind): string {
  switch (kind) {
    case "network":
      return NETWORK_MESSAGE;
    case "server":
      return SERVER_MESSAGE;
    case "client":
      return CLIENT_MESSAGE;
    default:
      return DEFAULT_MESSAGE;
  }
}

export function handleApiError(
  error: unknown,
  options: ApiErrorOptions | string = {},
): void {
  const opts: ApiErrorOptions =
    typeof options === "string" ? { fallbackMessage: options } : options;

  const { kind, status } = classifyApiError(error, opts.status);
  const message = opts.fallbackMessage ?? defaultMessageFor(kind);

  if (!opts.silent) toast.error(message);

  console.error("[api-error]", {
    source: opts.source ?? "unknown",
    kind,
    status,
    message: error instanceof Error ? error.message : String(error),
    error,
    ...(opts.context ?? {}),
  });
}
